import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Modal } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import CustomModal from "shared/components/Modal";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { USERS } from "utils/api/graphql/queries/users";
import { DATE_TIME_FORMAT, EVENT_TYPES, USER_TYPES } from "utils/constants";

function List({
  data = [],
  addEvent,
  updateEvent,
  deleteEvent,
  user,
  setFilter,
}) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.REMINDER);
  const LIST = t("LIST", { returnObjects: true });
  const DELETE = t("DELETE", { returnObjects: true });

  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState({});

  function renderEventContent(eventInfo) {
    return (
      <>
        <i>{eventInfo.event.title || LIST.NO_TITLE}</i>
      </>
    );
  }

  const handleDateSelect = (selectInfo) => {
    setModal(true);
    setCurrentEvent({
      start: get(selectInfo, "startStr"),
      end: get(selectInfo, "endStr"),
      allDay: get(selectInfo, "allDay"),
    });
  };

  const handleEventClick = (clickInfo) => {
    setDeleteModal(get(clickInfo, "event.id"));
  };

  const handleEventDelete = () => {
    deleteEvent({ variables: { where: { id: deleteModal } } });
  };

  const handleEventChange = (changeInfo) => {
    console.log("Event changed:", changeInfo.event);
  };

  const handleAddEvent = () => {
    addEvent({
      variables: {
        data: {
          id: cuid(),
          startDate: currentEvent.start,
          endDate: currentEvent.end,
          type: currentEvent.type,
          reminder: {
            id: cuid(),
            scheduledDate: currentEvent.start,
            content: currentEvent.title || "",
            user: {
              id: get(currentEvent, "user.id") || get(user, "id"),
            },
          },
        },
      },
    });
    setModal(false);
  };

  const onCancel = () => {
    setModal(false);
    setCurrentEvent({});
  };

  return (
    <div className="grid--1">
      <Header title={LIST.TITLE} />
      <Button onClick={() => setModal(true)}>{LIST.ADD_EVENT}</Button>
      <FullCalendar
        key={"calendar"}
        editable={false}
        droppable={false}
        eventStartEditable={false}
        eventDurationEditable={false}
        // scrollTime={moment().format(TIME_FORMAT)}
        locale={frLocale}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: "prev,today,next",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        buttonText={{
          today: LIST.TODAY,
          month: LIST.MONTH,
          week: LIST.WEEK,
          day: LIST.DAY,
          list: LIST.LIST,
        }}
        initialView={"timeGridWeek"}
        weekends={true}
        events={data.map(
          ({ id, startDate: start, endDate: end, reminder }) => ({
            id,
            title: get(reminder, "content", LIST.NO_TITLE),
            start,
            end,
          })
        )}
        eventContent={renderEventContent}
        selectable={true}
        select={handleDateSelect}
        eventClick={handleEventClick}
        eventChange={handleEventChange}
        // dateClick={handleDateClick}
        eventAdd={(e) => {
          console.log("eventAdd", e);
        }}
        eventRemove={(e) => {
          console.log("eventRemove", e);
        }}
        datesSet={(arg) => {
          setFilter((prev) => ({
            ...prev,
            gt: { startDate: arg.start },
            lt: { startDate: arg.end },
          }));
        }}
      />
      <Modal
        mode="simple"
        open={modal}
        onCancel={onCancel}
        onOk={handleAddEvent}
        title={LIST.ADD_EVENT}
      >
        <div className="grid--1">
          <Input
            placeholder={LIST.EVENT_TITLE}
            label={LIST.EVENT_TITLE}
            value={get(currentEvent, "title")}
            onChange={({ target: { value } }) =>
              setCurrentEvent((prev) => ({ ...prev, title: value }))
            }
          />
          <Input
            type="date"
            variant="range"
            label={LIST.DATES}
            showTime
            format={DATE_TIME_FORMAT}
            value={[
              dayjs(get(currentEvent, "start")),
              dayjs(get(currentEvent, "end")),
            ]}
            onChange={([start, end]) =>
              setCurrentEvent((prev) => ({ ...prev, start, end }))
            }
          />
          <Select
            options={Object.entries(EVENT_TYPES).map(([key, value]) => ({
              label: LIST.EVENT_TYPES[key],
              value,
            }))}
            placeholder={LIST.REMINDER_TYPE}
            label={LIST.REMINDER_TYPE}
            onChange={(value) =>
              setCurrentEvent((prev) => ({ ...prev, type: value }))
            }
          />
          <Select
            type="async"
            node={{
              query: USERS,
              variables: {
                take: null,
                exclude: ["telecoms", "attachments", "broker", "contact"],
                where: {
                  AND: [
                    { isActive: true },
                    {
                      OR: [
                        {
                          meta: { type: USER_TYPES.INTERNAL_ADVISOR.type },
                        },
                        {
                          meta: {
                            type: USER_TYPES.INTERNAL_SUPERVISOR.type,
                          },
                        },
                        { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
                        { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
                        {
                          meta: {
                            type: USER_TYPES.BROKER_REPRESENTATIVE.type,
                          },
                        },
                      ],
                    },
                  ],
                },
              },
              accessors: {
                root: "users.data",
                label: {
                  separator: " ",
                  keys: ["firstname", "lastname"],
                },
              },
            }}
            placeholder={LIST.RECIPIENT}
            label={LIST.RECIPIENT}
            onChange={(value) => {
              setCurrentEvent((prev) => ({ ...prev, user: { id: value } }));
            }}
          />
        </div>
      </Modal>

      <CustomModal
        okText={DELETE.OK}
        cancelText={DELETE.CANCEL}
        onCancel={() => setDeleteModal(false)}
        onOk={handleEventDelete}
        message={DELETE.MESSAGE}
        open={deleteModal}
      />
    </div>
  );
}

export default List;
